import * as React from 'react';

import { type TModifier } from '@/packages/Filters';
import { type NonEmptyArray, getDealTypeFromJsonQuery, getOfferTypeFromJsonQuery } from '@/packages/JsonQuery';

import { useReduxDispatch } from '@/microfrontends/filters/shared/store';
import { useJsonQuerySelector } from '@/microfrontends/filters/shared/store/slices/jsonQuery/selectors';
import { useLocationSelector } from '@/microfrontends/filters/shared/store/slices/location/selectors';
import { useRegionsSelector } from '@/microfrontends/filters/shared/store/slices/regions/selectors';
import { selectGeo } from '@/microfrontends/filters/shared/store/thunks/geo/selectGeo';
import { updateSearchMeta } from '@/microfrontends/filters/shared/store/thunks/searchMeta';
import { type IGeoObject, type IPolygonObject } from '@/microfrontends/filters/shared/types/LEGACY/geoSuggest';
import { useApplicationContext } from '@/microfrontends/filters/shared/utils/applicationContext';

import { GeoSuggest } from './GeoSuggest';
import { useSetHighways } from './hooks/useSetHighway';
import { useSetVillage } from './hooks/useSetVillage';
import { type ISelectedVillage } from './types';
import { getBoundedBy } from './utils';

export interface IGeoSuggestContainerProps {
  rightAddon?: React.ReactNode;
  onChange(...modifiers: NonEmptyArray<TModifier>): void;
  onApply(): void;
}

// TODO переписать и унести внутрь фильтров
export const GeoSuggestContainer: React.FC<IGeoSuggestContainerProps> = ({ rightAddon, onChange, onApply }) => {
  const ctx = useApplicationContext();
  const geoSuggestRef = React.useRef<GeoSuggest | null>(null);
  const dispatch = useReduxDispatch();

  const jsonQuery = useJsonQuerySelector();
  const regions = useRegionsSelector();
  const dealType = getDealTypeFromJsonQuery(jsonQuery.current);
  const offerType = getOfferTypeFromJsonQuery(jsonQuery.current);
  const location = useLocationSelector();
  const boundedBy = getBoundedBy(location.data, regions);
  const setVillage = useSetVillage();
  const selectHighways = useSetHighways();

  const handleGeoSelected = async (value: IGeoObject | IPolygonObject): Promise<void> => {
    const selectGeoResult = await dispatch(selectGeo(value));
    if (!selectGeo.fulfilled.match(selectGeoResult)) {
      return;
    }

    if (value.type === 'location' || value.type === 'regional_district_id') {
      // Пока что нельзя заменить на onChange, потому что его нужно сделать async и дожидаться.
      // onChange({ action: 'setJsonQuery', arguments: [selectGeoResult.payload] });
      await dispatch(updateSearchMeta());
      onApply();
    } else {
      onChange(
        { action: 'setJsonQuery', arguments: [selectGeoResult.payload] },
        { action: 'selectGeoFake', arguments: [] },
      );
    }
  };

  const handleHighwaySelected = async (highway: { id: number; regionId?: number; name: string }): Promise<void> => {
    geoSuggestRef.current?.startLoading();
    const modifiers = await selectHighways(highway);
    geoSuggestRef.current?.stopLoading();
    onChange(...modifiers);
  };

  const handleKPSelected = async (village: ISelectedVillage): Promise<void> => {
    geoSuggestRef.current?.startLoading();
    const modifiers = await setVillage(village);
    geoSuggestRef.current?.stopLoading();

    onChange(...modifiers);
  };

  return (
    <GeoSuggest
      boundedBy={boundedBy}
      context={ctx}
      currentLocation={location.data}
      dealType={dealType}
      offerType={offerType}
      ref={ref => (geoSuggestRef.current = ref)}
      rightAddon={rightAddon}
      onGeoSelected={handleGeoSelected}
      onHighwaySelected={handleHighwaySelected}
      onKPSelected={handleKPSelected}
    />
  );
};
